/**
 * 21/10/2026 — today's test drops of one route test ranked by the formula
 * being edited (POST ranking-preview, zero writes): debounced so a slider
 * drag asks once, the previous answer kept on screen while the next loads.
 * Asked only for a saved route test (the server needs its drops).
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { cleanHourFormula, lockRouteTestRanking, type DropGenGatewayRanking, type DropGenRouteTestRanking } from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../../../../useDropGenerator';

export type RouteTestPreview = DropGenRouteTestRanking & { drops: { sent: number; planned: number } };

/** The draft as the API takes it: today's dates locked, slider rows without a metric left out. */
export function previewBody(ranking: DropGenGatewayRanking): DropGenGatewayRanking {
  const locked = lockRouteTestRanking(ranking);
  return { ...locked, formula: cleanHourFormula(locked.formula) };
}

export function useRankingPreview(countryKey: string, containerId: string | null, ranking: DropGenGatewayRanking) {
  const live = JSON.stringify(previewBody(ranking));
  const bodyKey = useDebouncedValue(live, 400);
  const body = useMemo(() => JSON.parse(bodyKey) as DropGenGatewayRanking, [bodyKey]);
  const query = useQuery({
    queryKey: [...dropGeneratorKeys.all, 'route-test-ranking', countryKey, containerId, bodyKey] as const,
    queryFn: async () => {
      const res = await api.post<{ data: RouteTestPreview }>(`/drop-generator/route-tests/${countryKey}/${containerId}/ranking-preview`, body);
      return res.data.data;
    },
    enabled: Boolean(countryKey && containerId),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  return { ...query, pending: live !== bodyKey };
}
